import React, { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Printer, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import { formatVnDate } from "@apv-erp/status";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { trpcClient } from "@/lib/trpc";
import { formatNumber } from "@/lib/utils";
import { useCompanyConfig } from "@/hooks/useCompanyConfig";
import { getCompanyConfig } from "@/config/company";
import FinishedGoodsLabel, { type FinishedGoodsLabelData, LABEL_HEIGHT_MM, LABEL_WIDTH_MM } from "./FinishedGoodsLabel";

/**
 * In tem thùng số nhảy: chọn thùng theo từng lô, khổ cuộn 80 × 150 mm hoặc dàn A4 4 tem,
 * xem trước rồi in. Mở từ bảng Đóng gói (từng lô / hàng loạt) và từ Lệnh sản xuất (theo LSX).
 */
export interface CartonLabelPrintModalProps {
  open: boolean;
  onClose: () => void;
  /** Lô KCS (id / mã) — in tem của phiếu đóng gói tương ứng */
  qcFinalIds?: string[];
  /** Phiếu đóng gói (màn Đóng gói & Nhập kho) */
  packingSlipIds?: string[];
  productionOrderIds?: string[];
  /** Nút thêm ở chân hộp thoại (VD: chuyển sang tem pallet ở trang Đóng gói) */
  extraFooter?: React.ReactNode;
}

type PaperMode = "roll" | "a4";

interface LotLabels {
  qcFinalId: string;
  finalQcId: string;
  productionOrderCode?: string | null;
  customerName?: string | null;
  labels: (FinishedGoodsLabelData & { printedAt?: string | null })[];
}

function buildLots(res: any): LotLabels[] {
  return (res?.lots || []).map((lot: any) => {
    const total = lot.labels.length;
    const packingDate = lot.packingDate ? formatVnDate(lot.packingDate) : "";
    return {
      qcFinalId: lot.qcFinalId,
      finalQcId: lot.finalQcId,
      productionOrderCode: lot.productionOrderCode,
      customerName: lot.customerName,
      labels: lot.labels.map((l: any, idx: number) => ({
        labelId: l.id,
        labelCode: l.code,
        boxIndex: idx + 1,
        totalBoxes: total,
        boxQuantity: Number(l.quantity) || 0,
        isLastBox: idx === total - 1,
        unit: lot.unit,
        customerName: lot.customerName,
        orderCode: lot.orderCode,
        customerPo: lot.customerPo,
        productionOrderCode: lot.productionOrderCode,
        deliveryAddress: lot.deliveryAddress,
        productName: lot.productName,
        productCode: lot.productCode,
        lotNumber: lot.lotNumber,
        packer: lot.packer,
        packingDate,
        printedAt: l.printedAt,
      })),
    };
  });
}

const PAGE_CSS: Record<PaperMode, string> = {
  roll: `@page { size: ${LABEL_WIDTH_MM}mm ${LABEL_HEIGHT_MM}mm; margin: 0 }
    .sheet { display: block }
    .cell { width: ${LABEL_WIDTH_MM}mm; height: ${LABEL_HEIGHT_MM}mm; break-after: page; page-break-after: always; overflow: hidden }
    .cell:last-child { break-after: auto; page-break-after: auto }`,
  a4: `@page { size: A4 portrait; margin: 8mm }
    .sheet { display: grid; grid-template-columns: repeat(2, ${LABEL_WIDTH_MM}mm); grid-auto-rows: ${LABEL_HEIGHT_MM}mm; gap: 4mm 10mm; justify-content: center }
    .cell { overflow: hidden; outline: 0.2mm dashed #cbd5e1; break-inside: avoid }
    .cell:nth-child(4n) { break-after: page; page-break-after: always }`,
};

/** In qua iframe riêng: @page đúng khổ tem, không lẫn giao diện app. Nội dung tem dùng style nội tuyến. */
function printHtml(inner: string, mode: PaperMode): Promise<void> {
  // Kiểm thử E2E: nhận nội dung in thay cho hộp thoại in của trình duyệt
  const hook = (window as any).__apvPrintHook as ((html: string, css: string) => void) | undefined;
  if (hook) {
    hook(inner, PAGE_CSS[mode]);
    return Promise.resolve();
  }
  return new Promise((resolve) => {
    const iframe = document.createElement("iframe");
    iframe.setAttribute("aria-hidden", "true");
    Object.assign(iframe.style, { position: "fixed", right: "0", bottom: "0", width: "0", height: "0", border: "0" });
    document.body.appendChild(iframe);
    const doc = iframe.contentDocument!;
    doc.open();
    doc.write(`<!doctype html><html><head><meta charset="utf-8"><title>Tem thùng</title>
      <style>*{box-sizing:border-box;-webkit-print-color-adjust:exact;print-color-adjust:exact}html,body{margin:0;padding:0;background:#fff}${PAGE_CSS[mode]}</style>
      </head><body><div class="sheet">${inner}</div></body></html>`);
    doc.close();
    const imgs = Array.from(doc.images);
    const ready = Promise.all(
      imgs.map((img) => (img.complete ? Promise.resolve() : new Promise((r) => { img.onload = img.onerror = () => r(null); }))),
    );
    Promise.race([ready, new Promise((r) => setTimeout(r, 3000))]).then(() => {
      const win = iframe.contentWindow!;
      const cleanup = () => {
        setTimeout(() => iframe.remove(), 500);
        resolve();
      };
      win.onafterprint = cleanup;
      win.focus();
      win.print();
      // Safari / trình duyệt không bắn afterprint
      setTimeout(cleanup, 60_000);
    });
  });
}

export default function CartonLabelPrintModal({ open, onClose, qcFinalIds, packingSlipIds, productionOrderIds, extraFooter }: CartonLabelPrintModalProps) {
  const queryClient = useQueryClient();
  const company = getCompanyConfig(useCompanyConfig(open));
  const brand = { logoUrl: company.logoUrl, companyName: company.companyName, phone: company.phone, website: (company as any).website };
  const [lots, setLots] = useState<LotLabels[]>([]);
  const [skipped, setSkipped] = useState<{ code: string; reason: string }[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [mode, setMode] = useState<PaperMode>("roll");
  const [printing, setPrinting] = useState(false);
  const printRef = useRef<HTMLDivElement>(null);
  const requestKey = JSON.stringify({ qcFinalIds: qcFinalIds || [], packingSlipIds: packingSlipIds || [], productionOrderIds: productionOrderIds || [] });

  const prepare = useMutation({
    mutationFn: () => trpcClient.packingLabels.prepareForPrint.mutate({ qcFinalIds, packingSlipIds, productionOrderIds }),
    onSuccess: (res: any) => {
      const next = buildLots(res);
      setLots(next);
      setSkipped(res?.skipped || []);
      // Mặc định chọn thùng chưa in; tất cả đã in thì chọn hết (in lại)
      const all = next.flatMap((l) => l.labels);
      const unprinted = all.filter((l) => !l.printedAt);
      setSelected(new Set((unprinted.length ? unprinted : all).map((l) => l.labelId)));
    },
    onError: (err: any) => toast.error(err?.message || "Không lấy được dữ liệu tem thùng"),
  });

  useEffect(() => {
    if (open) {
      setLots([]);
      setSkipped([]);
      prepare.mutate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, requestKey]);

  const allLabels = useMemo(() => lots.flatMap((l) => l.labels), [lots]);
  const chosen = useMemo(() => allLabels.filter((l) => selected.has(l.labelId)), [allLabels, selected]);
  const chosenQty = chosen.reduce((s, l) => s + l.boxQuantity, 0);

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const setLot = (lot: LotLabels, on: boolean) =>
    setSelected((prev) => {
      const next = new Set(prev);
      lot.labels.forEach((l) => (on ? next.add(l.labelId) : next.delete(l.labelId)));
      return next;
    });

  const handlePrint = async () => {
    if (!printRef.current || chosen.length === 0) return;
    setPrinting(true);
    try {
      const cells = Array.from(printRef.current.querySelectorAll<HTMLElement>(".fg-label"))
        .map((el) => `<div class="cell">${el.outerHTML}</div>`)
        .join("");
      await printHtml(cells, mode);
      await trpcClient.packingLabels.markPrinted.mutate({ ids: chosen.map((l) => l.labelId) });
      const printedAt = new Date().toISOString();
      setLots((prev) =>
        prev.map((lot) => ({ ...lot, labels: lot.labels.map((l) => (selected.has(l.labelId) ? { ...l, printedAt } : l)) })),
      );
      queryClient.invalidateQueries({ queryKey: ["qcFinal"] });
      toast.success(`Đã gửi in ${chosen.length} tem thùng`);
    } catch (err: any) {
      toast.error(err?.message || "In tem thùng thất bại");
    } finally {
      setPrinting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-[1100px] w-[96vw] h-[90vh] p-0 flex flex-col gap-0" data-testid="carton-label-print-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Printer className="w-4 h-4" /> In tem thùng số nhảy
          </DialogTitle>
          <DialogDescription className="text-xs">
            {lots.length} lô • {allLabels.length} thùng • đang chọn {chosen.length} tem ({formatNumber(chosenQty)} SP)
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-2 px-5 py-2 border-b bg-slate-50 text-xs">
          <span className="text-slate-500">Khổ in:</span>
          {(
            [
              ["roll", "Cuộn 80 × 150 mm"],
              ["a4", "Dàn A4 (4 tem)"],
            ] as [PaperMode, string][]
          ).map(([key, label]) => (
            <Button key={key} size="sm" variant={mode === key ? "default" : "outline"} className="h-7 text-xs" onClick={() => setMode(key)}>
              {label}
            </Button>
          ))}
          <span className="mx-2 h-4 w-px bg-slate-200" />
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setSelected(new Set(allLabels.map((l) => l.labelId)))}>
            Chọn tất cả
          </Button>
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setSelected(new Set())}>
            Bỏ chọn
          </Button>
        </div>

        <div className="flex-1 min-h-0 flex flex-col md:flex-row">
          {/* Chọn thùng theo lô */}
          <div className="md:w-[360px] shrink-0 border-b md:border-b-0 md:border-r overflow-y-auto p-3 space-y-3 max-h-[35vh] md:max-h-none">
            {prepare.isPending && (
              <div className="flex items-center gap-2 text-sm text-slate-500 p-4">
                <Loader2 className="w-4 h-4 animate-spin" /> Đang chuẩn bị tem…
              </div>
            )}
            {skipped.length > 0 && (
              <div className="rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800 space-y-0.5" data-testid="carton-label-skipped">
                <div className="flex items-center gap-1 font-semibold">
                  <AlertTriangle className="w-3.5 h-3.5" /> Chưa in được
                </div>
                {skipped.map((s) => (
                  <div key={s.code}>
                    {s.code}: {s.reason}
                  </div>
                ))}
              </div>
            )}
            {!prepare.isPending && lots.length === 0 && (
              <div className="text-sm text-slate-500 p-4">Không có lô đã đóng gói & nhập kho để in tem.</div>
            )}
            {lots.map((lot) => {
              const picked = lot.labels.filter((l) => selected.has(l.labelId)).length;
              return (
                <div key={lot.qcFinalId} className="rounded-md border p-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-xs font-bold text-slate-900">
                        {lot.productionOrderCode} • {lot.finalQcId}
                      </div>
                      <div className="text-[11px] text-slate-500 break-words whitespace-normal">{lot.customerName || "—"}</div>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <button type="button" className="text-[11px] text-emerald-700 hover:underline" onClick={() => setLot(lot, true)}>
                        Tất cả
                      </button>
                      <button type="button" className="text-[11px] text-slate-500 hover:underline" onClick={() => setLot(lot, false)}>
                        Bỏ
                      </button>
                    </div>
                  </div>
                  <div className="text-[11px] text-slate-500 mt-1">
                    Đã chọn {picked}/{lot.labels.length} thùng
                  </div>
                  <div className="grid grid-cols-6 gap-1 mt-1.5">
                    {lot.labels.map((l) => {
                      const on = selected.has(l.labelId);
                      return (
                        <button
                          key={l.labelId}
                          type="button"
                          title={`${l.labelCode} • ${formatNumber(l.boxQuantity)} SP${l.printedAt ? " • đã in" : ""}`}
                          onClick={() => toggle(l.labelId)}
                          data-testid={`carton-box-${l.labelCode}`}
                          aria-pressed={on}
                          className={`relative h-7 rounded text-[11px] font-semibold border transition-colors ${
                            on ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-700 border-slate-200 hover:border-emerald-400"
                          } ${l.isLastBox && l.boxQuantity !== lot.labels[0]?.boxQuantity ? "ring-2 ring-amber-400" : ""}`}
                        >
                          {l.boxIndex}
                          {l.printedAt ? <CheckCircle2 className="absolute -top-1 -right-1 w-3 h-3 text-sky-500 bg-white rounded-full" /> : null}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
            {lots.length > 0 && (
              <div className="text-[11px] text-slate-500 flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3 text-sky-500" /> đã in trước đó • viền vàng: thùng cuối khác quy cách
              </div>
            )}
          </div>

          {/* Xem trước */}
          <div className="flex-1 min-h-0 overflow-auto bg-slate-100 p-4">
            {chosen.length === 0 ? (
              <div className="text-sm text-slate-500">Chọn thùng để xem trước tem.</div>
            ) : (
              <div ref={printRef} className="flex flex-wrap gap-4 justify-center" data-testid="carton-label-preview">
                {chosen.map((l) => (
                  // Xem trước nằm ngang cho dễ đọc; bản in giữ khổ dọc 80 × 150 mm của cuộn tem
                  <div
                    key={l.labelId}
                    className="shadow-sm border border-slate-300 bg-white overflow-hidden"
                    style={{ width: `${LABEL_HEIGHT_MM}mm`, height: `${LABEL_WIDTH_MM}mm` }}
                  >
                    <div style={{ transform: `translateY(${LABEL_WIDTH_MM}mm) rotate(-90deg)`, transformOrigin: "0 0" }}>
                      <FinishedGoodsLabel data={l} brand={brand} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t">
          {extraFooter ? <div className="mr-auto">{extraFooter}</div> : null}
          <Button variant="outline" onClick={onClose}>
            Đóng
          </Button>
          <Button onClick={handlePrint} disabled={printing || chosen.length === 0 || prepare.isPending} data-testid="carton-label-print-btn">
            {printing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Printer className="w-4 h-4 mr-1" />}
            In {chosen.length} tem
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
